import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { AskEdgentikPanel } from '../../pages/AskEdgentikPage';
import {
  SHOWCASE_CUSTOMER_DEMO5_LABEL,
  SHOWCASE_CUSTOMER_DEMO_LABEL,
  SHOWCASE2_PATH,
  showcaseAskBinding,
} from '../../domain/showcase/showcaseCustomerParity';
import { SHOWCASE_BOOTH_SHORTCUT_HREF } from '../../utils/warehouseNavigation';
import {
  SHOWCASE_BRAND,
  SHOWCASE_BRAND_LINE,
  showcaseEventLabel,
  SHOWCASE_LABEL_FALLBACK,
  SHOWCASE_PRIVACY,
  showcaseNowPane,
  showcaseSectionHeadline,
  showcaseStoryMode,
  showcaseVisitorPane,
  type ShowcasePane,
  type ShowcaseRoomBCondition,
  type ShowcaseSource,
} from '../../domain/showcase/showcasePresentation';

const ink = {
  bg: '#071018',
  text: '#f4f7fb',
  muted: '#8ea0b8',
  live: '#12744a',
  recorded: '#1d4ed8',
  demo: '#7c3aed',
  fallback: '#b45309',
};

export function ShowcaseBadge({
  label,
  tone,
  testId,
}: {
  label: string;
  tone: 'live' | 'recorded' | 'demo' | 'fallback';
  testId: string;
}) {
  const bg = tone === 'live' ? ink.live : tone === 'recorded' ? ink.recorded : tone === 'demo' ? ink.demo : ink.fallback;
  return (
    <Typography
      component="span"
      data-testid={testId}
      sx={{
        display: 'inline-block',
        bgcolor: bg,
        color: '#fff',
        fontWeight: 700,
        letterSpacing: 0.8,
        fontSize: 13,
        px: 0.9,
        py: 0.25,
        borderRadius: 0.5,
        mr: 0.6,
      }}
    >
      {label}
    </Typography>
  );
}

function CustomerBoothLink({
  testId,
  to,
  selected,
  label,
}: {
  testId: string;
  to: string;
  selected: boolean;
  label: string;
}) {
  return (
    <Box
      component={RouterLink}
      to={to}
      data-testid={testId}
      data-selected={selected ? 'true' : 'false'}
      aria-current={selected ? 'page' : undefined}
      sx={{
        color: selected ? '#f8fafc' : '#8ea0b8',
        fontWeight: 800,
        fontSize: 13,
        letterSpacing: 0.4,
        textDecoration: 'none',
        px: 1.25,
        py: 0.4,
        borderRadius: 0.5,
        border: selected ? '1px solid #f8fafc' : '1px solid rgba(255,255,255,0.18)',
        bgcolor: selected ? 'rgba(248,250,252,0.12)' : 'transparent',
      }}
    >
      {label}
    </Box>
  );
}

export function ShowcaseShell({
  pane,
  fallback,
  ppeEnabled,
  source = 'booth',
  roomBCondition = 'rae',
  lampMessage,
  children,
}: {
  pane: ShowcasePane;
  fallback: boolean;
  ppeEnabled: boolean;
  source?: ShowcaseSource;
  roomBCondition?: ShowcaseRoomBCondition;
  lampMessage?: string | null;
  children: React.ReactNode;
}) {
  const visitorPane = showcaseVisitorPane(pane, source);
  const selectedSource = visitorPane === 'forklift' ? 'room_b' : 'booth';
  const location = useLocation();
  const eventLabel = showcaseEventLabel(location.search);
  const [askOpen, setAskOpen] = useState(false);
  const askBinding = showcaseAskBinding(location.pathname);
  const onDemoUnit = location.pathname === SHOWCASE2_PATH;

  return (
    <Box
      data-testid="showcase-root"
      data-pane={pane}
      data-visitor-pane={visitorPane}
      data-mode={showcaseStoryMode(pane)}
      data-source={source}
      data-room-b-condition={roomBCondition}
      data-fallback={fallback ? 'true' : 'false'}
      data-ppe-enabled={ppeEnabled ? 'true' : 'false'}
      data-vms="false"
      sx={{
        position: 'fixed',
        inset: 0,
        height: '100dvh',
        maxHeight: '100dvh',
        width: '100vw',
        overflow: 'hidden',
        bgcolor: ink.bg,
        color: ink.text,
        px: { xs: 2, lg: 3 },
        pt: { xs: 0.75, lg: 1 },
        pb: { xs: 0.5, lg: 0.75 },
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flex: '0 0 auto' }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography data-testid="showcase-brand" sx={{ fontWeight: 800, letterSpacing: -0.3, fontSize: { xs: 20, lg: 24 }, lineHeight: 1 }}>
            {SHOWCASE_BRAND}
          </Typography>
          <Typography data-testid="showcase-brand-line" sx={{ color: ink.muted, fontSize: 13, mt: 0.2 }}>
            {SHOWCASE_BRAND_LINE}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right', minWidth: 0 }}>
          <Typography data-testid="showcase-event" sx={{ fontWeight: 650, fontSize: 13, color: '#c5d2e4' }}>
            {eventLabel}
          </Typography>
          {fallback ? <Box sx={{ mt: 0.25 }}><ShowcaseBadge label={SHOWCASE_LABEL_FALLBACK} tone="fallback" testId="showcase-label-fallback" /></Box> : null}
          <Typography data-testid="showcase-privacy" sx={{ color: ink.muted, fontSize: 13, maxWidth: 280, ml: 'auto', mt: 0.15 }}>
            {SHOWCASE_PRIVACY}
          </Typography>
        </Box>
      </Box>
      <Box data-testid="showcase-customer-nav" sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flex: '0 0 auto', mt: 0.75 }}>
        <CustomerBoothLink
          testId="showcase-customer-live"
          to={SHOWCASE_BOOTH_SHORTCUT_HREF}
          selected={!onDemoUnit}
          label={SHOWCASE_CUSTOMER_DEMO5_LABEL}
        />
        <CustomerBoothLink
          testId="showcase-customer-demo"
          to={SHOWCASE2_PATH}
          selected={onDemoUnit}
          label={SHOWCASE_CUSTOMER_DEMO_LABEL}
        />
        <Box
          component="button"
          type="button"
          data-testid="showcase-ask-open"
          onClick={() => setAskOpen((open) => !open)}
          sx={{
            ml: 'auto',
            border: '1px solid rgba(255,255,255,0.28)',
            bgcolor: 'rgba(248,250,252,0.12)',
            color: '#f8fafc',
            px: 1.25,
            py: 0.4,
            fontWeight: 800,
            fontSize: 13,
            cursor: 'pointer',
            borderRadius: 0.5,
          }}
        >
          Ask Edgentik
        </Box>
      </Box>
      {askOpen ? (
        <Box
          data-testid="showcase-ask-panel"
          sx={{
            position: 'absolute',
            top: 88,
            right: 16,
            width: 440,
            maxWidth: '92vw',
            maxHeight: '70dvh',
            overflow: 'auto',
            zIndex: 30,
            bgcolor: '#f8fafc',
            color: '#0f172a',
            borderRadius: 1,
          }}
        >
          <AskEdgentikPanel variant="embedded" product={askBinding} />
        </Box>
      ) : null}
      <Typography data-testid="showcase-section-headline" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
        {showcaseSectionHeadline(visitorPane === 'ppe' ? pane : showcaseNowPane(selectedSource))}
      </Typography>
      <Typography data-testid="showcase-title" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
        {SHOWCASE_BRAND}
      </Typography>

      {lampMessage ? (
        <Typography data-testid="showcase-lamp-test" sx={{ color: ink.muted, fontSize: 13, mt: 0.35, flex: '0 0 auto' }}>
          {lampMessage}
        </Typography>
      ) : null}

      <Box data-testid="showcase-stage" sx={{ flex: '1 1 auto', minHeight: 0, mt: 0.75, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {children}
      </Box>
    </Box>
  );
}
